{
  "app": {
    "title": "Schema Registry Portal"
  },
  "landing": {
    "title": "Standard definitions for data across the Autodesk ecosystem",
    "description": "The Autodesk Schema Registry Portal lets you discover and create data types for your data solutions, and supports a community that encourages data standardization across the Autodesk ecosystem of desktop products and APIs."
  },
  "home": {
    "welcome": "Welcome!",
    "description": "The Autodesk Schema Registry Portal is a single source of the truth for developers and non-developers data management professionals to discover, author, extend and manage their schemas, and data models.",
    "moreDescription": "Start exploring the schemas and data models used in the Autodesk ecosystem.",
    "subTitle": "Autodesk schemas",
    "subDescription": "A collection of built-in, reusable data types and components that are foundational to Autodesk products and applications and industry-specific data models."
  },
  "schema": {
    "schema": "Schema",
    "schemas": "Schemas",
    "editingDraft": "Editing Draft",
    "sortBy": "Sort by",
    "supportedSchemaTypes": "Supported schema types",
    "script": "Script",
    "schemaView": "Schema View",
    "interface": "Interface",
    "editor": {
      "saveAsDraft": "Save as Draft",
      "saveChanges": "Save changes",
      "saving": "Saving"
    },
    "table": {
      "header": {
        "schema": "Schema Name",
        "version": "Version",
        "schemaType": "Schema Type",
        "createTime": "Date Created"
      }
    }
  },
  "schemaTypeName": {
    "propertySets": "Property Sets",
    "entities": "Entities",
    "hubs": "Hubs",
    "projects": "Projects",
    "folders": "Folders",
    "items": "Items",
    "versions": "Versions",
    "refs": "Refs",
    "links": "Links",
    "contents": "Contents",
    "customTypes": "Custom Types",
    "commands": "Commands",
    "assetTemplates": "Asset Templates",
    "json": "JSON",
    "adskJson": "JSON",
    "gql": "GraphQL",
    "protobuf": "Protobuf",
    "auxiliary": "Auxiliary",
    "dependencies": "Dependencies",
    "derived": "Derived",
    "xrefs": "Xrefs"
  },
  "extendedSchemaTypeName": {
    "forgeDataSchema": "Forge Data Schema 2.0"
  },
  "views": {
    "pageNotFound": "Page Not Found",
    "search": "Search"
  },
  "group": {
    "group": "Group",
    "groups": "Groups",
    "noName": "No name",
    "createAction": "Create group",
    "addMoreDetails": "Add more details about this group.",
    "view": "View"
  },
  "library": {
    "library": "Library",
    "libraries": "Libraries",
    "noName": "No name",
    "createAction": "Create library",
    "createANewLibrary": "Create a new library",
    "addMoreDetails": "Add more details about this library."
  },
  "emptyScreen": {
    "group": {
      "title": "No library in the group '{{name}}' yet.",
      "content": {
        "admin": "This group doesn't have a library yet. Create your first library in this group to begin authoring and managing your schemas.",
        "write": "This group doesn't have a library yet. Create your first library in this group to begin authoring and managing your schemas.",
        "read": "This group doesn't have a library yet, and you don't have permission to create new libraries in this group. Contact the group admins for access to create libraries in this group.",
        "external": "This group doesn't have a library yet, and you don't have permission to create new libraries in this group."
      },
      "create": "Create library in {{name}}"
    },
    "library": {
      "title": "No schema in the library '{{name}}' yet.",
      "content": {
        "admin": "This library doesn't have a schema yet. Create your first schema in this library to define and structure the constituents of your data model.",
        "write": "This library doesn't have a schema yet. Create your first schema in this library to define and structure the constituents of your data model.",
        "read": "This library doesn't have a schema yet, and you don't have permission to create new schemas in this library. Contact the library admins for access to create schemas in this library.",
        "external": "This library doesn't have a schema yet, and you don't have permission to create new schemas in this library."
      },
      "create": "Create new schema"
    },
    "add": "Add member"
  },
  "create": {
    "title": "Create new {{entity}}",
    "createEntityInContainer": "Create {{entity}} in {{containerName}}",
    "noPermissionToCreateEntity": "You do not have permission to create {{entities}} in this {{association}}.",
    "displayTitle": {
      "label": "Display Title",
      "tooltip": "The 'Display Title' appears as the main heading of the {{entity}} on the UI. It can be modified later. It corresponds to the 'Title' field in the API."
    },
    "systemID": {
      "label": "System ID",
      "tooltip": "The 'System ID' can't be changed once created, as it serves as the unique identifier of this {{entity}} across the system. It corresponds to the 'Name' field in the API.",
      "helperHint": "You can manually change the auto-generated system ID. You can use letters, numbers, and the underscore(_) character.",
      "helperErrorAlreadyExists": "This system ID already exists. Please try another by manually changing the auto-generated system ID using letters, numbers and the underscore(_) character.",
      "helperErrorInvalid": "This format cannot be accepted as a system ID. Please try another by using letters, numbers, and the underscore(_) character."
    },
    "description": {
      "label": "Description",
      "optional": "(Optional)",
      "placeholder": "Add a brief description summarizing about this {{entity}}."
    },
    "successMessage": "The '{{entityTitle}}' {{entity}} is successfully created in the '{{containerTitle}}' {{container}}",
    "failureMessage": "Fail to create {{entity}}",
    "failureMessageWithReason": "Fail to create {{entity}}: {{error}}"
  },
  "admin": {
    "pageTitle": "Manage access and members",
    "addMembers": "Add members",
    "removeMember": "Remove member",
    "enterEmail": "Enter email addresses or client Ids",
    "invalidEmail": "One or more email addresses do not represent a valid Autodesk account.",
    "selectPermission": "Select permission level",
    "addMembersSuccessfully": "Members are added successfully",
    "addMembersFailure": "Fail to add members: {{error}}",
    "entityViewingAccess": {
      "organization": "Organization viewing access",
      "group": "Group viewing access",
      "library": "Library viewing access"
    },
    "readOnly": "read-only",
    "peopleWhoCanView": "People who you or other group admins specify can view",
    "anyoneCanView": "Anyone with Autodesk account can view",
    "viewingAccessTooltip": {
      "organization": "Users with organization viewing access can see details like the organization name, creation time, and any available documentation for the organization. They can also get information about the groups within the organization.",
      "group": "Users with group viewing access can see details like the group name, creation time, and any available documentation for the group. They can also get information about the libraries within the group.",
      "library": "Users with library viewing access can see details like the library name, creation time, and any available documentation for the library. They can also get information about the schemas within the library."
    },
    "noPermissionToChangeViewingAccess": "You do not have permission to change viewing access."
  },
  "access": {
    "Permission": "Permission",
    "admin": "Admin",
    "write": "Write",
    "read": "Read",
    "delete": "Delete",
    "viewEntityDetails": "View {{entity}} details",
    "queryForEntityDetails": "Query for {{entity}} details",
    "queryItemsInEntity": "Query for all {{items}} in {{entity}}",
    "createItemInGroup": "Create {{item}} in {{entity}}",
    "updateEntityDetails": "Update {{entity}} details",
    "updatePermissions": "Update {{entity}} permissions",
    "deleteEntity": "Delete {{entity}}",
    "deleteItemInEntity": "Delete {{item}} in {{entity}}",
    "alert": {
      "group": "Access to group '{{name}}' is denied.",
      "library": "Access to library '{{name}}' is denied."
    },
    "noPermission": {
      "group": "You do not have access to the group '{{name}}'. Contact the group admins for access to this group.",
      "library": "You do not have access to the library '{{name}}'. Contact the library admins for access to this library."
    },
    "noPermissionToPerformAction": "You do not have permission to perform this action."
  },
  "management": {
    "common": {
      "button": "Manage access and members",
      "member": "member",
      "addMembers": "Add members",
      "removeMember": "Remove member",
      "removeYourself": "Remove yourself",
      "itIsYou": "(you)",
      "clientIdLabel": "(Client Id)",
      "removeAdminDisabled": "You can't remove yourself without assigning others with admin permission.",
      "nameColumn": "Name",
      "filterPlaceholder": "Filter members",
      "otherMembers": "{{hiddenCount}} others",
      "otherPermissions": "and {{permissions}} permissions given",
      "creationInfo": "Created on {{dateString}} at {{timeString}} by <strong>{{memberName}}</strong>",
      "infoTooltip": "A member can have one or more permissions at a time. The read permission only lets them view content and doesn't allow for any actions. Members with admin, write, and delete permissions can do more things based on their given privileges.",
      "permissionName": {
        "admin": "admin",
        "write": "write",
        "delete": "delete",
        "read": "read"
      }
    },
    "organization": {
      "heading": "Organization members",
      "permissionColumn": "Organization Permissions",
      "adminLabel": "Organization admin",
      "memberListTitle": "Organization members",
      "noMemberFound": "No member found to be given explicit permissions in this organization."
    },
    "group": {
      "heading": "Group members",
      "permissionColumn": "Group Permissions",
      "adminLabel": "Group admin",
      "memberListTitle": "Group members",
      "noMemberFound": "No member found to be given explicit permissions in this group."
    },
    "library": {
      "heading": "Library members",
      "permissionColumn": "Library Permissions",
      "adminLabel": "Library admin",
      "memberListTitle": "Library members",
      "noMemberFound": "No member found to be given explicit permissions in this library."
    },
    "removeMember": {
      "others": {
        "title": "Remove this member from the {{entity}} '{{entityName}}'?",
        "message": "Removed members will lose given permissions specifically assigned by a group admin. Do you want to proceed and remove <strong>{{memberName}}</strong> from {{entityName}}?"
      },
      "yourself": {
        "title": "Remove yourself from the {{entity}} '{{entityName}}'?",
        "message": "You are about to remove yourself (<strong>{{email}}</strong>) from the group. Once removed, you will lose given permissions specifically assigned by a group admin. Do you want to proceed and remove yourself from {{entityName}}?"
      }
    }
  },
  "menus": {
    "createByInheritance": "Create schema by inheriting from this schema",
    "createByCopy": "Create schema by copying this schema",
    "editDraft": "Edit draft",
    "deleteSchema": "Delete schema",
    "copyPermlink": "Copy permalink URL"
  },
  "buttons": {
    "signIn": "Sign in",
    "cancel": "Cancel",
    "copy": "Copy",
    "publish": "Publish",
    "close": "Close",
    "save": "Save",
    "ok": "OK",
    "discardAllChanges": "Discard all changes",
    "discardChanges": "Discard changes",
    "publishAsVersion": "Publish as version",
    "viewDraft": "View draft",
    "createDraft": "Create draft",
    "deleteDraft": "Delete draft",
    "deletePublishedSchema": "Delete published schema",
    "sortBy": "Sort by",
    "reloadPage": "Reload Page",
    "learnMore": "Learn More",
    "validate": "Validate",
    "wrap": "Wrap",
    "unwrap": "Unwrap",
    "apply": "Apply"
  },
  "progress": {
    "deleting": "Deleting...",
    "loading": "Loading...",
    "saving": "Saving...",
    "processing": "Processing..."
  },
  "labels": {
    "redirectOuthSignIn": "Redirecting to Autodesk Login",
    "progressSigningIn": "Please wait while we sign you in",
    "verifyingSignIn": "Verifying Autodesk Login",
    "signingOut": "Please wait while we sign you out",
    "creatingNewSchema": "CREATING NEW SCHEMA",
    "editingDraft": "EDITING DRAFT",
    "filterByOrganization": "Filter by Organization",
    "search": "Search",
    "new": "New"
  },
  "dialogs": {
    "publishVersionAsDefaultTitle": "Publish as a version",
    "publishVersionAsDefault": "Upon publishing the schema, this version will appear as the latest version by default. Continue to publish this schema version?",
    "publishVersionWithoutSavingDraftTitle": "Publish as a version",
    "publishVersionWithoutSavingDraft": "You are about to publish without saving a draft.",
    "versionDescription": "Version Description",
    "semanticVersion": "Version",
    "deleteDraftTitle": "Delete draft schema",
    "deleteSchemaTitle": "Delete published schema",
    "deleteSchemaPrompt": "Are you sure you want to delete \"{{schema}}\"? You cannot undo this action.",
    "filterByOrganizationAtLeastOne": "At least one (1) organization must be selected.",
    "changeSchemaType": "Change Schema Type",
    "changeSchemaTypePrompt": "Discard all changes and switch to the new schema type?",
    "discardChangesPrompt": "Discard all changes and return to previous page?"
  },
  "alerts": {
    "loginRequired": "Login is required to access this page.",
    "oauthCannotRetrieveCode": "Failed to retrieve Access Token from OAuth.",
    "oauthCannotRefreshCode": "Failed to retrieve Refresh Token from OAuth.",
    "filterContentLoadingError": "We are having problem loading the filter categories. Try reloading the page.",
    "inheritanceRepoLoadingError": "We are having problem loading the inheritance repositories. Try reloading the page.",
    "mobileScreenNotSupported": "Sorry, mobile screen (portrait mode) is currently not supported. Please view this on a screen of more than 768px in width.",
    "schemaSavingError": "We are not able to save your schema. Please try again.",
    "schemaPublishingError": "We are not able to publish your schema. Please try again.",
    "schemaPublishedSuccess": "{{schemaId}} has been published.",
    "draftDeleted": "The draft schema \"{{schemaId}}\" is successfully deleted from '{{groupId}} > {{libraryId}}'.",
    "schemaDeleted": "The published schema \"{{schemaId}}\" is successfully deleted from '{{groupId}} > {{libraryId}}'.",
    "schemaIsNotValid": "Schema is not valid.",
    "schemaIsValid": "Schema is valid.",
    "schemaValidationFailed": "Schema validation has failed.",
    "draftSavedSuccessfully": "Draft has been saved successfully.",
    "draftNotFound": "There's no saved draft for this schema. You will be creating a new draft.",
    "draftVersionMustBeValidTitle": "You're editing a draft version that is not published.",
    "draftVersionMustBeValid": "A draft version would require valid semantic versioning (SemVer) to be successfully saved as a draft. The first digit must be greater than 0.",
    "newSchemaVersionMustBeValidTitle": "You're creating a draft version that is not published.",
    "newSchemaVersionMustBeValid": "A schema would require valid semantic versioning (SemVer) to be successfully saved. The first digit must be greater than 0.",
    "noGroupFoundInOrganization": "No group found in this organization.",
    "noSchemaFoundInLibrary": "No schema found in this library.",
    "schemaNotFound": "The schema you're trying to access does not exist.",
    "tooManySearchResults": "Too many results. Total results will be capped at 10,000.",
    "viewingOlderVersion": "You are viewing an older version of this schema.",
    "schemaNameChangeNotAllowed": "Changing the schema name is not allowed when creating a new version of the same schema.",
    "somethingWentWrong": "Oops! Something went wrong.",
    "reloadPageToContinue": "A problem occurred while trying to display this page. Please reload or visit a different page to continue.",
    "pageNotFoundTitle": "Page Not Found",
    "pageNotFound": "The page you're trying to access is not found, or you do not have access to view it.",
    "noSchemaLoaded": " There is no schema loaded in this request. Please reload the page.",
    "noTokenFound": "There is no token loaded in this request. Please reload the page.",
    "successfullyPublished": "{{resourceId}} has been has been published.",
    "emptySchemaNameNotAllowed": "Schema name must not be empty.",
    "typeidMustBeInTheForm": "typeid must be in the form of 'your.namespace:schema.name-1.0.0'",
    "typeidIsRequired": "typeid is required.",
    "typeidMustEndWithValidVersion": "typeid must end with a valid version number, e.g. '-1.0.1'. Suggested format: 1.0.0 / 1.0.0-alpha / 1.0.0-beta.1 / 1.0.0-2023.01.31",
    "schemaContentCannotBeEmpty": "Schema content cannot be empty.",
    "schemaSpecMismatch": "'schemaSpecification' must be in the form of 'forge-data-schema-x.y.z' where x, y and z are positive numbers, and x is a non-zero value",
    "schemaTypeMismatch": "'schemaType' must be specified and must be one of 'component', 'enum' or 'entity'",
    "semanticVersionFormatNotSupported": "The versioning format you input is not supported.\nSuggested format: 1.0.0 / 1.0.0-alpha / 1.0.0-beta.1 / 1.0.0-2023.01.31",
    "schemaTypeCannotBeEmpty": "Schema type cannot be empty.",
    "invalidJsonSchemaId": "Invalid '$id' value specified: '{{id}}'",
    "invalidJsonSchemaSchema": "'$schema' must be a non-empty string value",
    "permalinkCopied": "Permalink URL Copied!",
    "changesSaved": "Changes to {{entity}} has been saved.",
    "changesFailedToSaved": "Failed to save {{entity}}.",
    "requestHeaderFieldsTooLarge": "Request header is too large. Please try clearing cookies for this site and reloading the page."
  },
  "tooltips": {
    "editingDraftSchema": "You are editing the draft schema.",
    "changesWillOverwriteSavedDraft": "Any changes you save will overwrite the previously saved draft of this schema.",
    "changeRepository": "Change Group",
    "createNewSchema": "Create new schema",
    "noPermissionToCreateNewSchema": "You do not have permission to create schema in this library.",
    "noPermissionToEditDraftSchema": "You do not have permission to edit or publish draft in this library.",
    "versionDescription": "While this field is optional, it is highly encouraged to describe changes made to this version.",
    "schemaValidationNotSupported": "Schema validation for {{schemaType}} format is done when saving the schema using draft/publish button.",
    "semanticVersion": "A versioning convention that follows proper semantic versioning (SemVer) is required to publish this schema successfully."
  },
  "footer": {
    "privacySettings": "Privacy settings",
    "doNotSellPersonalInfo": "Do not sell my personal information",
    "privacyCookie": "Privacy / Cookies",
    "copyright": "© 2026 Autodesk Inc. All rights reserved"
  },
  "versionHistory": {
    "title": "Version History",
    "version": "Version",
    "latest": "latest",
    "edited": "edited",
    "deleted": "deleted",
    "publishedTime": "Published on {{date}} at {{time}}",
    "createdBy": "by {{name}}",
    "noVersionDescription": "No version description available.",
    "noVersionHistory": "No version history.",
    "textFieldLabel": "Version Description",
    "savedSuccessfully": "Version description saved successfully.",
    "goToVersion": "Go to this version"
  }
}
